/**
 * VPV Buy Box Card Handler
 * Manages buy box card selection and ATC functionality
 * Namespaced with vpv- prefix
 */

class VpvBuyBoxCards {
  constructor(container) {
    this.container = container;
    this.cards = container.querySelectorAll('[data-vpv-buy-box-card]');
    this.buyButton = container.querySelector('[data-vpv-add-to-cart]');
    this.priceDisplay = this.buyButton?.querySelector('[data-vpv-price]');
    this.variantRadios = container.querySelector('variant-radios');
    
    this.activeCard = null;
    this.init();
  }

  init() {
    if (!this.cards.length) return;

    // Set up card click handlers
    this.cards.forEach(card => {
      const header = card.querySelector('.vpv-buy-box-card__header');
      if (header) {
        header.addEventListener('click', () => this.selectCard(card));
      }
    });

    // Find and activate default card
    const defaultCard = this.container.querySelector('.vpv-buy-box-card--active') || this.cards[0];
    if (defaultCard) {
      this.selectCard(defaultCard, false);
    }

    // Set up buy button
    if (this.buyButton) {
      this.buyButton.addEventListener('click', (e) => this.handleAddToCart(e));
    }

    // Listen for variant changes from swatch clicks
    window.addEventListener('vpv:variant:changed', (e) => this.handleVariantChange(e.detail));
    window.addEventListener('variant:changed', (e) => this.handleVariantChange(e.detail));
  }

  handleVariantChange(detail) {
    if (!detail || !this.activeCard) return;

    const isLinkedProduct = this.activeCard.dataset.isLinkedProduct === 'true';
    
    if (isLinkedProduct) {
      // For linked products, try to match the selected color to a variant
      const linkedVariantsJson = this.activeCard.dataset.linkedVariants;
      if (!linkedVariantsJson) {
        console.log('[VPV] Linked product has no color variants map, using default');
        return;
      }

      // Get the selected color from the variant change event
      let selectedColor = null;
      if (detail.swatch) {
        selectedColor = detail.swatch.dataset?.limited || 
                        detail.swatch.dataset?.color ||
                        detail.swatch.textContent?.trim();
      }
      if (!selectedColor && detail.color) selectedColor = detail.color;
      if (!selectedColor && detail.title) selectedColor = detail.title;
      if (!selectedColor && detail.value) selectedColor = detail.value;
      if (!selectedColor && detail.option1) selectedColor = detail.option1;

      if (!selectedColor) {
        console.log('[VPV] No color found in variant change for linked product');
        return;
      }

      // Normalize the color for lookup
      const normalizedColor = selectedColor.toLowerCase().trim().replace(/\s+/g, '-');

      try {
        const linkedVariants = JSON.parse(linkedVariantsJson);
        const matchedVariantId = linkedVariants[normalizedColor];

        if (matchedVariantId) {
          const variantData = this.activeCard.querySelector('.vpv-buy-box-card__variant-data');
          if (variantData) {
            variantData.dataset.variantId = matchedVariantId;
            console.log('[VPV] Linked product variant updated to match color:', normalizedColor, '→', matchedVariantId);
          }
        } else {
          console.log('[VPV] No matching variant found for color:', normalizedColor, 'in linked product');
        }
      } catch (e) {
        console.error('[VPV] Error parsing linked variants:', e);
      }
      return;
    }

    // Standard behavior for non-linked products
    const variantId = detail.variantId || detail.id;
    if (!variantId) return;

    const variantData = this.activeCard.querySelector('.vpv-buy-box-card__variant-data');
    if (variantData) {
      variantData.dataset.variantId = variantId;
    }

    console.log('[VPV] Variant updated in active card:', variantId);
  }

  selectCard(card, animate = true) {
    // Deselect all cards
    this.cards.forEach(c => {
      c.classList.remove('vpv-buy-box-card--active');
      const content = c.querySelector('.vpv-buy-box-card__content');
      if (content && animate) {
        content.style.maxHeight = '0';
      }
    });

    // Select clicked card
    card.classList.add('vpv-buy-box-card--active');
    const content = card.querySelector('.vpv-buy-box-card__content');
    if (content) {
      content.style.maxHeight = content.scrollHeight + 'px';
    }

    this.activeCard = card;
    this.updatePriceDisplay();
    
    // Sync variant based on currently selected color swatch
    const isLinkedProduct = card.dataset.isLinkedProduct === 'true';
    const selectedSwatch = this.container.querySelector('.swatch-input:checked, .is-color.selectedColor');
    
    console.log('[VPV] Card selected:', {
      isLinkedProduct,
      hasLinkedVariants: !!card.dataset.linkedVariants,
      selectedSwatch: selectedSwatch?.value || selectedSwatch?.dataset?.color || 'none found'
    });
    
    if (selectedSwatch) {
      if (isLinkedProduct) {
        // For linked products, match the color to linked product's variant
        const linkedVariantsJson = card.dataset.linkedVariants;
        if (linkedVariantsJson) {
          let selectedColor = selectedSwatch.dataset?.limited || 
                              selectedSwatch.dataset?.color ||
                              selectedSwatch.value ||
                              selectedSwatch.textContent?.trim();
          
          if (selectedColor) {
            const normalizedColor = selectedColor.toLowerCase().trim().replace(/\s+/g, '-');
            try {
              const linkedVariants = JSON.parse(linkedVariantsJson);
              const matchedVariantId = linkedVariants[normalizedColor];
              
              console.log('[VPV] Linked product color matching:', {
                selectedColor,
                normalizedColor,
                availableColors: Object.keys(linkedVariants),
                matchedVariantId: matchedVariantId || 'NO MATCH'
              });
              
              if (matchedVariantId) {
                const variantData = card.querySelector('.vpv-buy-box-card__variant-data');
                if (variantData) {
                  variantData.dataset.variantId = matchedVariantId;
                  console.log('[VPV] ✓ Linked product variant set to:', matchedVariantId);
                }
              }
            } catch (e) {
              console.error('[VPV] Error parsing linked variants on select:', e);
            }
          }
        }
      } else {
        // For regular cards, sync the variant ID from the swatch
        const variantId = selectedSwatch.dataset?.varid || selectedSwatch.value;
        if (variantId) {
          const variantData = card.querySelector('.vpv-buy-box-card__variant-data');
          if (variantData) {
            variantData.dataset.variantId = variantId;
          }
        }
      }
    }

    // Dispatch event for other components (like buy bar) to listen to
    document.dispatchEvent(new CustomEvent('vpv:card:selected', {
      detail: { card: card, index: card.dataset.index }
    }));
  }

  updatePriceDisplay() {
    if (!this.priceDisplay || !this.activeCard) return;

    const priceEl = this.activeCard.querySelector('[data-vpv-current-price]');
    if (priceEl) {
      this.priceDisplay.textContent = priceEl.textContent;
    }
  }

  getSectionsToRender() {
    const sections = [];

    const cartContent = document.getElementById('cart-drawer__content');
    if (cartContent) {
      sections.push({
        id: 'cart-drawer__content',
        section: cartContent.dataset.id,
        selector: '.cart-drawer__content',
      });
    }

    if (document.getElementById('cart-icon-bubble')) {
      sections.push({
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      });
    }

    const cartHeader = document.getElementById('cart-drawer__header');
    if (cartHeader) {
      sections.push({
        id: 'cart-drawer__header',
        section: cartHeader.dataset.id,
        selector: '.cart-drawer__header',
      });
    }

    const cartFooter = document.getElementById('cart-drawer__footer');
    if (cartFooter) {
      sections.push({
        id: 'cart-drawer__footer',
        section: cartFooter.dataset.id,
        selector: '.cart-drawer__footer',
      });
    }

    return sections;
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML || '';
  }

  async handleAddToCart(e) {
    e.preventDefault();

    if (!this.activeCard) return;

    const variantData = this.activeCard.querySelector('.vpv-buy-box-card__variant-data');
    if (!variantData) return;

    const variantId = variantData.dataset.variantId;
    const cardProductHandle = variantData.dataset.cardProductHandle;
    const sellingPlanId = variantData.dataset.sellingPlanId;
    const isSubscription = variantData.dataset.isSubscription === 'true';
    const bundleProducts = variantData.dataset.bundleProducts;
    const isLinkedProduct = this.activeCard.dataset.isLinkedProduct === 'true';
    const mainProductHandle = this.activeCard.dataset.productHandle;

    console.log('[VPV] ╔════════════════════════════════════');
    console.log('[VPV] ║ ADD TO CART DEBUG');
    console.log('[VPV] ╠════════════════════════════════════');
    console.log('[VPV] ║ Main product handle:', mainProductHandle);
    console.log('[VPV] ║ Card product handle:', cardProductHandle);
    console.log('[VPV] ║ Is linked product:', isLinkedProduct);
    console.log('[VPV] ║ Variant ID being added:', variantId);
    console.log('[VPV] ║ Selling Plan ID:', sellingPlanId);
    console.log('[VPV] ║ Is Subscription:', isSubscription);
    console.log('[VPV] ╚════════════════════════════════════');

    let items = [{
      id: parseInt(variantId),
      quantity: 1
    }];

    // Add selling plan if subscription
    if (isSubscription && sellingPlanId) {
      items[0].selling_plan = parseInt(sellingPlanId);
    }

    // Handle bundle products if present
    if (bundleProducts) {
      try {
        const bundleItems = JSON.parse(bundleProducts);
        items = bundleItems.map(item => ({
          id: parseInt(item.id),
          quantity: 1,
          properties: item.hide === 'true' ? { '_hidden': 'true' } : {}
        }));
      } catch (error) {
        console.error('[VPV] Error parsing bundle products:', error);
      }
    }

    // Disable button during request
    this.buyButton.disabled = true;
    this.buyButton.classList.add('vpv-loading');

    const cartDrawer = document.querySelector('cart-drawer');

    // The drawer only opens once this request lands (see below) — the
    // button's own vpv-loading state (CSS: assets/vpv-main-product.css) is
    // what signals "something is happening" in the meantime, so there's no
    // stale/empty-cart flash to cover for with an early, data-less open
    // anymore.
    //
    // Same staleness guard cart-drawer.js's own mutations use (see
    // CartDrawer.beginRequest/isStaleRequest) — without it, this request's
    // eventual render could land after, and silently revert, a newer action
    // elsewhere that has no way to know this one was in flight.
    const requestId = cartDrawer?.beginRequest ? cartDrawer.beginRequest() : null;

    try {
      const sectionsToRender = this.getSectionsToRender();

      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/javascript'
        },
        body: JSON.stringify({
          items: items,
          sections: sectionsToRender.map(s => s.section),
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Add to cart failed');
      }

      const data = await response.json();

      if (requestId != null && cartDrawer.isStaleRequest(requestId)) return;

      // Update cart sections via the shared helper (assets/cart-drawer.js) so
      // the footer gets its surgical patch — not a wholesale innerHTML swap —
      // keeping in-flight state (a discount code being typed, etc.) intact.
      if (data.sections) {
        sectionsToRender.forEach(section => {
          renderCartSection(section, data.sections[section.section]);
        });
      }

      // Opened only now, with the added item already painted into the
      // drawer's sections above — a normal open() (no skipSideEffects) is
      // safe here since the cart data is already current, and its own
      // "cart:updated" dispatch covers cart-protection-sync.js.
      this.openCartDrawer();

      // Dispatch custom event for cart drawer/notification
      document.dispatchEvent(new CustomEvent('vpv:cart:item-added', {
        detail: { items: data.items || [data] }
      }));

      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { items: data.items || [data] }
      }));

    } catch (error) {
      console.error('[VPV] Add to cart error:', error);
      
      const errorEvent = new CustomEvent('vpv:cart:error', {
        detail: { message: error.message || 'Could not add item to cart. Please try again.' }
      });
      document.dispatchEvent(errorEvent);

    } finally {
      this.buyButton.disabled = false;
      this.buyButton.classList.remove('vpv-loading');
    }
  }

  /**
   * Public method for secondary ATC buttons to delegate to.
   * Temporarily swaps buyButton so loading state applies to the
   * calling button, then restores after the cart request completes.
   */
  async addToCartForButton(btn) {
    const originalBuyButton = this.buyButton;
    this.buyButton = btn;
    const fakeEvent = { preventDefault: () => {} };
    await this.handleAddToCart(fakeEvent);
    this.buyButton = originalBuyButton;
  }

  openCartDrawer(options) {
    // Try multiple methods to open the cart drawer
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open(options);
      return;
    }

    // Fallback: try to find and click the cart icon
    const cartIcon = document.querySelector('[data-cart-icon], .cart-icon-bubble, #cart-icon-bubble a');
    if (cartIcon) {
      cartIcon.click();
      return;
    }

    // Another fallback: set open attribute
    if (cartDrawer) {
      cartDrawer.setAttribute('open', '');
      cartDrawer.classList.add('is-open', 'active');
    }

    console.log('[VPV] Cart drawer opened');
  }
}

// Initialize on DOM ready
function initVpvBuyBoxCards() {
  // Only initialize on .vpv-product__form-container (not the outer .vpv-main-product)
  // This prevents double initialization since form-container is inside main-product
  const containers = document.querySelectorAll('.vpv-product__form-container');
  containers.forEach(container => {
    if (!container.vpvBuyBoxCards) {
      container.vpvBuyBoxCards = new VpvBuyBoxCards(container);
      console.log('[VPV] Buy box cards initialized');
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVpvBuyBoxCards);
} else {
  initVpvBuyBoxCards();
}

// Re-initialize on section reload (Shopify editor)
document.addEventListener('shopify:section:load', (e) => {
  if (e.target.classList.contains('vpv-product-section') || e.target.querySelector('.vpv-main-product')) {
    initVpvBuyBoxCards();
  }
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = VpvBuyBoxCards;
}

// Expose to window for debugging
window.VpvBuyBoxCards = VpvBuyBoxCards;

// =============================================================================
// VPV Secondary ATC Button Handler
// =============================================================================
/**
 * Keeps page-level ATC buttons (lower-page CTAs, inline ATCs, etc.)
 * in sync with the active VpvBuyBoxCards selection and fires the same
 * cart-add logic — including selling plan, loading state, section
 * rendering, and cart drawer — on click.
 *
 * Replaces the old [data-product-atc-wrap] / .no-sub__atc pattern from
 * global.js which read variant state from .is-color.selectedColor.
 *
 * NOTE: The sticky buy bar (vpv-buy-bar custom element) is intentionally
 * excluded — it manages its own state via VpvBuyBar.
 *
 * Targets (any button NOT inside .vpv-product__form-container or vpv-buy-bar):
 *   [data-buy-bar-button]                                    – injected page ATCs (third-party/app)
 *   [data-native-pre-order-btn]:not([data-vpv-add-to-cart])  – secondary ATCs
 *   .no-sub__atc                                             – legacy class
 */
class VpvSecondaryAtcButtons {
  constructor() {
    this.SECONDARY_SELECTOR = [
      '[data-buy-bar-button]',
      '[data-native-pre-order-btn]:not([data-vpv-add-to-cart])',
      '.no-sub__atc'
    ].join(', ');

    this.buttons = [];
    this.vpvCards = null;

    this._setup = this._setup.bind(this);
    this._syncButtons = this._syncButtons.bind(this);

    this._registerEventListeners();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => setTimeout(this._setup, 0));
    } else {
      // Defer one tick so VpvBuyBoxCards finishes its own init first
      setTimeout(this._setup, 0);
    }
  }

  _registerEventListeners() {
    // Keep secondary buttons in sync whenever card selection or variant changes
    document.addEventListener('vpv:card:selected', this._syncButtons);
    window.addEventListener('vpv:variant:changed', this._syncButtons);
    window.addEventListener('variant:changed', this._syncButtons);

    // Re-init on Shopify editor section reload
    document.addEventListener('shopify:section:load', (e) => {
      if (e.target.querySelector('.vpv-main-product, .vpv-product__form-container')) {
        setTimeout(this._setup, 0);
      }
    });
  }

  _getCardsInstance() {
    return document.querySelector('.vpv-product__form-container')?.vpvBuyBoxCards || null;
  }

  _setup() {
    this.vpvCards = this._getCardsInstance();

    // Only activate on VPV pages
    if (!this.vpvCards) {
      console.log('[VPV Secondary] No VpvBuyBoxCards found, skipping secondary button setup');
      return;
    }

    // Query secondary buttons: outside .vpv-product__form-container
    const allMatches = [...document.querySelectorAll(this.SECONDARY_SELECTOR)];
    this.buttons = allMatches.filter(btn =>
      !btn.closest('.vpv-product__form-container') &&
      !btn.closest('vpv-buy-bar')
    );

    if (!this.buttons.length) {
      console.log('[VPV Secondary] No secondary buttons found');
      return;
    }

    // Bind click handlers (guard against double-binding)
    this.buttons.forEach(btn => {
      if (btn._vpvSecondaryBound) return;
      btn._vpvSecondaryBound = true;
      
      // Remove any inline onclick handlers
      btn.onclick = null;
      
      // Use capture phase to intercept BEFORE global.js handlers
      btn.addEventListener('click', (e) => this._handleClick(e, btn), true);
    });

    this._syncButtons();
    // Deferred re-sync: VpvBuyBoxCards.selectFirstVariant() runs in a
    // setTimeout(0), so activeCard may not be set when _setup first fires.
    setTimeout(() => this._syncButtons(), 150);
    console.log(`[VPV Secondary] Bound ${this.buttons.length} secondary ATC button(s)`);
  }

  /**
   * Sync price display and disabled state from the active card.
   * Falls back gracefully when VpvBuyBoxCards is not present (e.g.
   * product has no buy_box_options metafield).
   */
  _syncButtons() {
    if (!this.vpvCards) this.vpvCards = this._getCardsInstance();

    this.buttons.forEach(btn => {
      if (this.vpvCards?.activeCard) {
        // --- VPV path: read from active card ---
        const priceEl = this.vpvCards.activeCard.querySelector('[data-vpv-current-price]');
        if (priceEl) {
          const price = priceEl.textContent.trim();
          const priceSpan = btn.querySelector('[data-vpv-price], [data-price]');
          if (priceSpan) {
            priceSpan.textContent = price;
          } else {
            // Plain-text injected button (no nested spans) — rewrite full label
            const atcLabel = window.variantStrings?.addToCart || 'Add to Cart';
            btn.textContent = `${price} - ${atcLabel}`;
          }
        }

        const variantData = this.vpvCards.activeCard.querySelector('.vpv-buy-box-card__variant-data');
        btn.disabled = !variantData?.dataset?.variantId;

      } else {
        // --- Legacy fallback: read from .is-color.selectedColor ---
        const selectedColor = document.querySelector('.is-color.selectedColor');
        if (selectedColor) {
          const available = selectedColor.dataset?.available;
          if (available) {
            const priceSpan = btn.querySelector('[data-vpv-price], [data-price]');
            if (priceSpan) {
              priceSpan.textContent = available;
            } else {
              const atcLabel = window.variantStrings?.addToCart || 'Add to Cart';
              btn.textContent = `${available} - ${atcLabel}`;
            }
          }
          btn.disabled = !selectedColor.dataset?.varid;
        }
      }
    });
  }

  async _handleClick(e, btn) {
    // CRITICAL: Stop propagation to prevent global.js handlers from firing
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    if (!this.vpvCards) this.vpvCards = this._getCardsInstance();

    if (this.vpvCards?.activeCard) {
      // --- VPV path: delegate to VpvBuyBoxCards ---
      console.log('[VPV Secondary] Delegating to VpvBuyBoxCards');
      await this.vpvCards.addToCartForButton(btn);
      return;
    }

    // --- Legacy fallback: mirror old .no-sub__atc / [data-product-atc-wrap] logic ---
    console.log('[VPV Secondary] Using legacy fallback');
    const atcWrap = btn.closest('[data-product-atc-wrap]');
    let variantId;

    if (atcWrap) {
      const valsEl = atcWrap.querySelector('.vals');
      if (valsEl && ['Denomination', 'Amounts'].includes(valsEl.dataset.id)) {
        variantId = Number(atcWrap.querySelector('.vals input:checked')?.getAttribute('data-varid'));
      } else {
        variantId = Number(atcWrap.querySelector('.is-color.selectedColor')?.getAttribute('data-varid'));
      }
    } else {
      variantId = Number(document.querySelector('.is-color.selectedColor')?.getAttribute('data-varid'));
    }

    if (!variantId) {
      console.warn('[VPV Secondary] No variant ID found, cannot add to cart');
      return;
    }

    btn.disabled = true;
    btn.classList.add('vpv-loading');

    const cartDrawer = document.querySelector('cart-drawer');

    // The drawer only opens once this request lands (see below) — the
    // button's own vpv-loading state is what signals "something is
    // happening" in the meantime.
    //
    // Same staleness guard cart-drawer.js's own mutations use (see
    // CartDrawer.beginRequest/isStaleRequest) — without it, this request's
    // eventual render could land after, and silently revert, a newer action
    // elsewhere that has no way to know this one was in flight.
    const requestId = cartDrawer?.beginRequest ? cartDrawer.beginRequest() : null;

    try {
      const sections = this._getSectionsToRender();
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: sections.map(s => s.section),
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.description || 'Add to cart failed');
      }

      const data = await response.json();

      if (requestId != null && cartDrawer.isStaleRequest(requestId)) return;

      if (data.sections) {
        sections.forEach(section => {
          renderCartSection(section, data.sections[section.section]);
        });
      }

      // Opened only now, with the added item already painted into the
      // drawer's sections above — a normal open() (no skipSideEffects) is
      // safe since the cart data is already current, and its own
      // "cart:updated" dispatch covers cart-protection-sync.js.
      cartDrawer?.open?.();

    } catch (err) {
      console.error('[VPV Secondary] Legacy fallback ATC error:', err);
    } finally {
      btn.disabled = false;
      btn.classList.remove('vpv-loading');
    }
  }

  _getSectionsToRender() {
    const sections = [];
    const cartContent = document.getElementById('cart-drawer__content');
    if (cartContent) sections.push({ id: 'cart-drawer__content', section: cartContent.dataset.id, selector: '.cart-drawer__content' });
    if (document.getElementById('cart-icon-bubble')) sections.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    const cartHeader = document.getElementById('cart-drawer__header');
    if (cartHeader) sections.push({ id: 'cart-drawer__header', section: cartHeader.dataset.id, selector: '.cart-drawer__header' });
    const cartFooter = document.getElementById('cart-drawer__footer');
    if (cartFooter) sections.push({ id: 'cart-drawer__footer', section: cartFooter.dataset.id, selector: '.cart-drawer__footer' });
    return sections;
  }
}

// Initialize secondary ATC button handler
function initVpvSecondaryAtcButtons() {
  if (!window.vpvSecondaryAtcButtons) {
    window.vpvSecondaryAtcButtons = new VpvSecondaryAtcButtons();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVpvSecondaryAtcButtons);
} else {
  initVpvSecondaryAtcButtons();
}

// Expose for debugging
window.VpvSecondaryAtcButtons = VpvSecondaryAtcButtons;
